import { get } from 'lodash';
import { TrustApplicantRelatedPartyType } from '@lendi/core-constants';
import { Accordion, AccordionGroup } from '@lendi-ui/accordion';
import { Heading } from '@lendi-ui/typography';

import {
  FIELD_PATHS,
  FIELD_VALUES,
  PII_FIELD_MASKING_CLASS,
  RELATED_PARTY_TRUST_OWNER_TYPE_LABELS,
  YES_NO_OPTIONS,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { RelatedParty, StoreApplicant } from '@funnels/types';
import { FunnelGrid, FunnelGridUnit } from '@funnels/ui-components';
import { HelpTooltip } from '@funnels/ui-components/_common';
import { getApplicantName, initialFormValues } from '@funnels/utils';

import { DropdownField } from '../../Fields';
import UncontrolledRadioButtonsField from '../../Fields/_uncontrolled/RadioButtonsField';
import { AccordionContentWrapper } from '../../Layouts/ReadOnly/_common/style';
import { RELATED_PARTY_OWNER_ROLE_LABELS } from '../CompanyDirectors';

interface TrustTrusteesProp {
  trustees: RelatedParty[];
  trustApplicantClientId: string;
  opened: boolean[];
  setOpened: (value: boolean[]) => void;
}

export const TrustTrustees = ({ trustees, trustApplicantClientId, opened, setOpened }: TrustTrusteesProp) => {
  const [formValues, setFormValues] = useFormValues();
  const { tracking } = useAnalytics();

  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);

  function handleAccordionClick(index: number, title: string) {
    const arr = [...opened];
    tracking?.accordion[arr[index] ? 'collapsed' : 'expanded']({ name: title, position: title });
    arr[index] = !arr[index]; // toggle
    setOpened(arr);
  }
  const handleBeneficiaryUpdate = (
    e: React.ChangeEvent<HTMLInputElement>,
    trusteeApplicantId: string,
    trustBeneficiaryClientId?: string
  ) => {
    const value = e.target.value;
    if (value === FIELD_VALUES.TRUE) {
      setFormValues(FIELD_PATHS.RELATED_PARTIES(), [
        ...relatedParties,
        initialFormValues.INITIAL_TRUST_BENEFICIARY_RELATED_PARTY(trustApplicantClientId, trusteeApplicantId),
      ]);
    } else {
      setFormValues(
        FIELD_PATHS.RELATED_PARTIES(),
        relatedParties.filter(
          (_, index) => get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_CLIENT_ID(index)) !== trustBeneficiaryClientId
        )
      );
    }
  };

  const handleBeneficialOwnerUpdate = (
    e: React.ChangeEvent<HTMLInputElement>,
    trusteeApplicantId: string,
    trustBeneficialOwnerClientId?: string
  ) => {
    const value = e.target.value;
    if (value === FIELD_VALUES.TRUE) {
      setFormValues(FIELD_PATHS.RELATED_PARTIES(), [
        ...relatedParties,
        initialFormValues.INITIAL_TRUST_BENEFICIAL_OWNER_RELATED_PARTY(trustApplicantClientId, trusteeApplicantId),
      ]);
    } else {
      setFormValues(
        FIELD_PATHS.RELATED_PARTIES(),
        relatedParties.filter(
          (_, index) =>
            get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_CLIENT_ID(index)) !== trustBeneficialOwnerClientId
        )
      );
    }
  };

  const beneficiaryTooltip = `Beneficiaries may have an entitlement to trust income or capital that is set out in the trust deed or they may acquire an entitlement because the trustee exercises a discretion to pay them income or capital.`;

  const beneficialOwnerTooltip = `A beneficial owner is an individual that owns or controls the entity. Control may be by acting as trustee or by exercising control through some other means. 
  ‘Owns’ in this case means owning 25% or more (directly or indirectly) of the entity.
  ‘Controls’ may be by acting as trustee or by exercising control through some other means.`;

  const beneficialOwnerTypeTooltip = `There must be at least 1 controller on the application.`;

  return (
    <AccordionContentWrapper>
      <AccordionGroup variant="empty">
        {trustees.map((trustee: RelatedParty, index) => {
          const trusteeApplicantId = get(
            { relatedParties: [trustee] },
            FIELD_PATHS.RELATED_PARTY_APPLICANT_CLIENT_ID(0)
          );
          const applicantName = getApplicantName(applicants, trusteeApplicantId);
          const companyName = get(
            { applicants },
            FIELD_PATHS.APPLICANT_COMPANY_NAME(
              applicants.findIndex((a: StoreApplicant) => a.id === trustee.applicantClientId)
            )
          );
          const heading = companyName || applicantName || `Trustee ${index + 1}`;
          const pii = companyName || applicantName;
          const trustBeneficiaryIndex = relatedParties.findIndex(
            (relatedParty) =>
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_CLIENT_ID(0)) ===
                trusteeApplicantId &&
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_CLIENT_ID(0)) ===
                trustApplicantClientId &&
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
                TrustApplicantRelatedPartyType.TrustBeneficiary
          );
          const trustBeneficialOwnerIndex = relatedParties.findIndex(
            (relatedParty) =>
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_CLIENT_ID(0)) ===
                trusteeApplicantId &&
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_CLIENT_ID(0)) ===
                trustApplicantClientId &&
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
                TrustApplicantRelatedPartyType.TrustBeneficialOwner
          );

          return (
            <AccordionContentWrapper key={`trustee-${trustee.clientId}`}>
              <Accordion isOpen={opened[index]}>
                <Accordion.Header
                  onClick={() => handleAccordionClick(index, 'Trustee details')}
                  data-component="accordion-header"
                >
                  <Heading size="xxs" className={PII_FIELD_MASKING_CLASS}>
                    {heading}
                  </Heading>
                </Accordion.Header>
                <Accordion.Content data-component="accordion-content">
                  <FunnelGrid>
                    <FunnelGridUnit mb="md">
                      <UncontrolledRadioButtonsField
                        label={`Is ${heading} a beneficiary?`}
                        piiStringInLabel={pii}
                        labelAfter={<HelpTooltip content={beneficiaryTooltip} />}
                        radios={YES_NO_OPTIONS}
                        direction="row"
                        onChange={(e) =>
                          handleBeneficiaryUpdate(
                            e,
                            trusteeApplicantId,
                            get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_CLIENT_ID(trustBeneficiaryIndex))
                          )
                        }
                        selectedValue={trustBeneficiaryIndex !== -1}
                      />
                    </FunnelGridUnit>
                    <FunnelGridUnit mb="md">
                      <UncontrolledRadioButtonsField
                        label={`Is ${heading} a beneficial owner?`}
                        piiStringInLabel={pii}
                        labelAfter={<HelpTooltip content={beneficialOwnerTooltip} />}
                        radios={YES_NO_OPTIONS}
                        direction="row"
                        onChange={(e) =>
                          handleBeneficialOwnerUpdate(
                            e,
                            trusteeApplicantId,
                            get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_CLIENT_ID(trustBeneficialOwnerIndex))
                          )
                        }
                        selectedValue={trustBeneficialOwnerIndex !== -1}
                      />
                    </FunnelGridUnit>
                    {trustBeneficialOwnerIndex !== -1 && (
                      <>
                        <FunnelGridUnit mb="md">
                          <DropdownField
                            labelAfter={<HelpTooltip content={beneficialOwnerTypeTooltip} />}
                            label="Beneficial owner type"
                            name={FIELD_PATHS.RELATED_PARTY_OWNER_TYPE(trustBeneficialOwnerIndex)}
                            options={RELATED_PARTY_TRUST_OWNER_TYPE_LABELS}
                            isOptional
                          />
                        </FunnelGridUnit>
                        <FunnelGridUnit mb="md">
                          <DropdownField
                            label="Beneficial owner role"
                            name={FIELD_PATHS.RELATED_PARTY_OWNER_ROLE(trustBeneficialOwnerIndex)}
                            options={RELATED_PARTY_OWNER_ROLE_LABELS}
                            isOptional
                          />
                        </FunnelGridUnit>
                      </>
                    )}
                  </FunnelGrid>
                </Accordion.Content>
              </Accordion>
            </AccordionContentWrapper>
          );
        })}
      </AccordionGroup>
    </AccordionContentWrapper>
  );
};
